import {type ReactNode, useId, useRef} from 'react';

import {AppButton} from '@/components/Button';
import CloseButton from '@/components/CloseButton/CloseButton';
import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import {StickyPageLayout} from '@/components/Layout';
import PopupResponsive from '@/components/Popup/PopupResponsive';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import type {PopupModel} from '@/types/aem/popupModel';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

interface AutoDeliveryResultPopupProps {
    popup: PopupModel;
    description?: string;
    onClose: () => void;
    onButtonClick: () => void;
    children?: ReactNode;
}

const AutoDeliveryResultPopup = ({
    popup,
    description,
    onClose,
    onButtonClick,
    children,
}: AutoDeliveryResultPopupProps) => {
    const headerId = useId();
    const popupRef = useRef<HTMLElement | null>(null);
    const isMobile = useIsMobileLayout();

    const {icon, title, description: fallbackDescription, primaryButton} = popup;
    const displayDescription = description ?? fallbackDescription;

    return (
        // @ts-expect-error PopupResponsive is not fully typed
        <PopupResponsive
            overlayRef={popupRef}
            onClose={onClose}
            className='z-[21] flex flex-col bg-romance ml:ml-[calc(var(--sidebar-width)/2)] ml:w-full ml:max-w-[494px] ml:rounded-xl ml:p-8 ml:pt-19 ml:text-center'
            aria-labelledby={headerId}
        >
            {isMobile && (
                <WarningBannersTop source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY} containerRef={popupRef} />
            )}
            {/* @ts-expect-error */}
            <StickyPageLayout
                contentClassName='flex flex-1 flex-col'
                desktopContentClassName='flex flex-col items-center'
                desktopFooterClassName='flex flex-col items-center'
                headerOptions={{
                    // @ts-expect-error CloseButton is not typed
                    endIcon: <CloseButton onClick={onClose} />,
                }}
                footer={
                    primaryButton && (
                        <AppButton
                            primary
                            light
                            className='mt-0 ml:w-full'
                            onClick={onButtonClick}
                            aria-label={primaryButton.alt}
                        >
                            {primaryButton.text}
                        </AppButton>
                    )
                }
            >
                <div className='flex flex-1 flex-col items-center justify-center gap-2 p-6 ml:pb-8 ml:pt-3'>
                    {icon && <RemoteIconV2 icon={icon} className='size-16' />}
                    {title && (
                        <h1 id={headerId} className='m-0 text-28 leading-[40px]'>
                            {title}
                        </h1>
                    )}
                    {displayDescription && <p className='text-16 leading-[24px] tracking-02'>{displayDescription}</p>}
                    {children}
                </div>
                {isMobile && (
                    // @ts-expect-error
                    <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.AUTO_DELIVERY} />
                )}
            </StickyPageLayout>
        </PopupResponsive>
    );
};

export default AutoDeliveryResultPopup;
